import { MedewerkersOpStation, useGetMedewerkersOpStationAanvraag } from "@/entities/medewerkers-op-station";
import { useGetStationDisplayName } from "@/entities/station";
import { DossierSummaryCard, DossierSummaryCardSkeleton } from "@/features/dossier-details";
import { Badge, Dot, EmptyState, ErrorDisplay, Icon, Separated } from "@/shared/components";
import { getListPreview } from "@/shared/utils/list-preview";
import { statusMap } from "../../constants";

const getGeaccepteerdeVossenCount = (station: MedewerkersOpStation) => station.vossen.filter((vos) => vos.status === "Geaccepteerd").length;

type MedewerkersOpStationSummaryProps = {
    dossierNummer: string;
};

export function MedewerkersOpStationSummary({ dossierNummer }: MedewerkersOpStationSummaryProps) {
    const { data: aanvraag, isPending, error } = useGetMedewerkersOpStationAanvraag(dossierNummer);

    if (isPending) {
        return <DossierSummaryCardSkeleton section="MedewerkersOpStation" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van medewerkers op station" />;
    }

    const relevantStations = aanvraag.filter((station) => station.status !== "NietAangevraagd" || station.mollen.length > 0);

    const geaccepteerdeVossenCount = relevantStations.reduce((acc, station) => {
        return acc + getGeaccepteerdeVossenCount(station);
    }, 0);

    const status =
        geaccepteerdeVossenCount > 0 ? `${geaccepteerdeVossenCount} VOS${geaccepteerdeVossenCount > 1 ? "'en" : ""} actief` : undefined;

    const hasContent = relevantStations.length > 0;

    return (
        <DossierSummaryCard section="MedewerkersOpStation" status={status}>
            {hasContent ? (
                <MedewerkersOpStationSummaryContent stations={relevantStations} />
            ) : (
                <EmptyState className="pr-3 pl-4">Geen medewerkers aangevraagd</EmptyState>
            )}
        </DossierSummaryCard>
    );
}

type MedewerkersOpStationSummaryContentProps = {
    stations: MedewerkersOpStation[];
};

function MedewerkersOpStationSummaryContent({ stations }: MedewerkersOpStationSummaryContentProps) {
    const getStationDisplayName = useGetStationDisplayName();
    const sortedStations = stations.toSorted((a, b) => {
        const aHasGeaccepteerdeVossen = getGeaccepteerdeVossenCount(a) > 0;
        const bHasGeaccepteerdeVossen = getGeaccepteerdeVossenCount(b) > 0;

        // Stations with geaccepteerde vossen come first, sorted alphabetically by station name
        if (aHasGeaccepteerdeVossen !== bHasGeaccepteerdeVossen) {
            return aHasGeaccepteerdeVossen ? -1 : 1;
        }

        if (aHasGeaccepteerdeVossen && bHasGeaccepteerdeVossen) {
            return getStationDisplayName(a.stationCode).localeCompare(getStationDisplayName(b.stationCode));
        }

        // sort the rest by status

        const aIsLopendeAanvraag = a.status === "AanvraagLoopt";
        const bIsLopendeAanvraag = b.status === "AanvraagLoopt";

        // Stations with lopende aanvragen come second
        if (aIsLopendeAanvraag !== bIsLopendeAanvraag) {
            return aIsLopendeAanvraag ? -1 : 1;
        }

        const aIsAfgerond = a.status === "Gestopt" || a.status === "GeenVosInzetbaar";
        const bIsAfgerond = b.status === "Gestopt" || b.status === "GeenVosInzetbaar";

        // Stations that are gestopt / geenVosInzetbaar come third
        if (aIsAfgerond !== bIsAfgerond) {
            return aIsAfgerond ? -1 : 1;
        }

        // Then sort alphabetically by station name
        return getStationDisplayName(a.stationCode).localeCompare(getStationDisplayName(b.stationCode));
    });

    const { previewItems: displayStations, remainder } = getListPreview(sortedStations, 5);

    return (
        <div className="flex flex-col gap-2 pr-3 pl-4">
            <ul data-testid="medewerkers-op-station-summary-ul">
                <Separated>
                    {displayStations.map((station) => (
                        <MedewerkersOpStationSummaryItem
                            key={station.stationCode}
                            station={station}
                            getStationDisplayName={getStationDisplayName}
                        />
                    ))}
                </Separated>
            </ul>
            {remainder > 0 && (
                <span data-testid="medewerkers-op-station-summary-remainder" className="text-s text-content-subtle">
                    + nog {remainder} stations
                </span>
            )}
        </div>
    );
}

type MedewerkersOpStationSummaryItemProps = {
    station: MedewerkersOpStation;
    getStationDisplayName: (stationCode: string) => string;
};

function MedewerkersOpStationSummaryItem({ station, getStationDisplayName }: MedewerkersOpStationSummaryItemProps) {
    const geaccepteerdeVossenCount = getGeaccepteerdeVossenCount(station);
    const mollenCount = station.mollen.length;

    const status = station.status !== "NietAangevraagd" ? statusMap[station.status] : null; // we may have stations with status NietAangevraagd if there are only mollen

    return (
        <li
            className="flex items-center gap-2 py-2 first:pt-0 last:border-b only:border-b-0"
            data-testid={`medewerkers-op-station-summary-li-${station.stationCode}`}
        >
            <span className="text-content-emphasis text-m font-medium">{getStationDisplayName(station.stationCode)}</span>
            <Dot size="s" variant="emphasis" />
            <span>{station.bemenst ? "Bemenst" : "Onbemenst"}</span>
            {mollenCount > 0 && (
                <>
                    <Dot size="s" variant="emphasis" />
                    <span data-testid="mol-count">
                        {mollenCount} MOL{mollenCount > 1 ? "'en" : ""}
                    </span>
                </>
            )}
            {status && (
                <p className="ml-auto flex items-center gap-2">
                    {status.label}
                    {geaccepteerdeVossenCount > 0 && (
                        <Badge className="flex gap-1 border">
                            <Icon name="user" size="xs" />
                            {geaccepteerdeVossenCount}
                        </Badge>
                    )}
                    <Dot variant={status.dotVariant} />
                </p>
            )}
        </li>
    );
}
